import * as React from 'react'
import { ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Modal from '@components/modal/Modal'
import Icon from '@components/icon/Icons'
import Button from '@components/button/Button'
import Radio, { RadioOption } from '@components/radio/Radio'
import Drawer from '@components/drawer/Drawer'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import theme from '@theme/Theme'

interface Props {
  submitStatus: Status
  onSubmit: (response: UserResponse) => void
  options: RadioOption[]
}

const SelectTechnician = ({ submitStatus, onSubmit, options }: Props) => {
  const i18n = useI18n()
  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const [technician, setTechnician] = React.useState<RadioOption>(options[0])

  const renderForm = () => (
    <FormContainer scrollIndicatorInsets={{ right: 1 }} bounces={false} keyboardShouldPersistTaps="handled">
      <SubTitle>{i18n.t('screens.incidentDetail.selectTechnician.available')}</SubTitle>
      <RadioContainer>
        <Radio iconOnTheRight data={options} value={technician} onItemSelected={setTechnician} />
      </RadioContainer>
    </FormContainer>
  )

  if (isPhone) {
    return (
      <Container>
        <Title isCentered>{i18n.t('screens.incidentDetail.selectTechnician.title')}</Title>
        <CloseButton activeOpacity={0.9} onPress={Drawer.close}>
          <Icon name="close" size={24} color={theme.colors.primary} />
        </CloseButton>
        {renderForm()}
        <ButtonContainer width={width}>
          <Button
            status={submitStatus === 'loading' ? 'loading' : 'active'}
            label={i18n.t('common.validate')}
            onClick={() => {
              onSubmit(technician.value)
              Drawer.close()
            }}
          />
        </ButtonContainer>
      </Container>
    )
  }

  return (
    <ModalContainer>
      <ModalHeader>{i18n.t('screens.incidentDetail.selectTechnician.title')}</ModalHeader>

      <CloseButton activeOpacity={0.9} onPress={() => Modal.close()}>
        <Icon name="close" size={24} color={theme.colors.primary} />
      </CloseButton>

      {renderForm()}
      <ModalButton>
        <Button
          status={submitStatus === 'loading' ? 'loading' : 'active'}
          label={i18n.t('common.validate')}
          onClick={() => {
            onSubmit(technician.value)
            Modal.close()
          }}
        />
      </ModalButton>
    </ModalContainer>
  )
}

export default SelectTechnician

const Container = styled(View)`
  padding-bottom: 16px;
  flex: 1;
`
const Title = styled(Text)<{ isCentered?: boolean }>`
  margin-bottom: 24px;
  ${(props) => props.theme.fonts.Headline};
  ${(props) => props.isCentered && 'text-align:center;'}
`

const SubTitle = styled(Text)`
  margin-bottom: 8px;
  ${(props) => props.theme.fonts.Caption1};
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) => props.theme.breakpoint === 'phone' && `padding: 0px 16px;`}
`

const ButtonContainer = styled(View)<{ width: number }>`
  position: absolute;
  left: 0;
  bottom: 0;
  padding: 16px;
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background};
`
const CloseButton = styled(TouchableOpacity)`
  padding: 16px;
  position: absolute;
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
      top: -18px;
      left: 0px;`
      : `
      top: 0px;
      right: 0px;`}
`

const ModalContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.incident};
  width: 650px;
  height: 500px;
`
const FormContainer = styled(ScrollView)`
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
        padding-bottom: 100px;
        `
      : `
        background-color: ${props.theme.colors.background};
        padding: 35px 24px;
        `}
`
const ModalHeader = styled(Text)`
  padding: 24px 24px 36px;
  ${(props) => props.theme.fonts.Title3};
`
const ModalButton = styled(View)`
  padding: 24px;
  width: 280px;
  align-self: center;
`

const RadioContainer = styled(View)`
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `
    background-color: ${props.theme.colors.background};
    padding: 0px 16px;
    `
      : `
    padding: 0px 0px 0px 16px;
    `}
  border-top-width: 1px;
  border-bottom-width: 1px;
  border-color: ${(props) => props.theme.colors.disable};
  border-style: solid;
`
